import {
  EConstructionType,
  EOpportunityCriteria,
  EPropertyCriteriaSort,
  EPropertyType,
  IProperty,
  IPropertyPhoto,
} from '../../../types-library/src'
import { IHttpResponse } from '../types/httpTypes'
import { listingsBasicApi } from '@wiggot/apis'

interface IGetPropertiesByParametersParams {
  address?: string
  allowEmptyCoordinates?: boolean
  amenities?: string
  arrayCoordinates?: string
  associationIds?: string
  bathrooms?: number
  bedrooms?: number
  boundsFilter?: string
  budgetHighRange?: number
  budgetLowRange?: number
  budgetM2HighRange?: number
  budgetM2LowRange?: number
  buildType?: EConstructionType
  builtYearFrom?: number
  builtYearTo?: number
  caprateHighRange?: number
  caprateLowRange?: number
  coldStart?: number
  commissionRateHighRange?: number
  commissionRateLowRange?: number
  discountHighRange?: number
  discountLowRange?: number
  drawingFilter?: string
  geocertainty?: 'low'
  keywords?: string[]
  latitude?: number
  listingsSource?: 'all' | 'wiggot'
  longitude?: number
  neighborhoods?: string
  opportunityCriteria?: EOpportunityCriteria
  origin?: string
  page?: number
  pamRequestId?: string
  parkings?: number
  perPage?: number
  presale?: boolean
  proxyPublishedAt?: string
  proxyPublishedDays?: string
  quickLinkState?: string
  radiusMeters?: number
  searchAddress?: string
  searchLevel?: string
  sessions?: string
  showOnlyCommissionProperties?: boolean
  sortCriteria?: EPropertyCriteriaSort
  surfaceFrom?: number
  surfaceTo?: number
  type?: EPropertyType
  wiggotUserType?: 'full' | 'macrobolsa'
}

interface IPropertiesResponse {
  currentPage: number
  globalCount: number
  items: IProperty[]
  perpage: number
  totalItems: number
  totalPages: number
}

interface INewAPIResponse {
  pagination: {
    page: number
    perPage: number
    totalItems: number
    totalPages: number
  }
  items: INewPropertyItem[]
}

interface INewPropertyItem {
  id: string
  address: string
  media: Array<{ url: string }>
  bathrooms: string
  bedrooms: number
  priceAmount: string
  constructionSurface: string
  propertyType: string
  slug: string
  surfaceDetails: {
    unit: string
    floors: number | null
    terrace: number | null
    terrain: number
    construction: number
    pricePerSquareMeter: number
  }
  terrainSurface: string
  coordinates: {
    x: number
    y: number
  }
  isWiggotProperty: boolean
  ads: Array<{
    id: string
    propertyId: string
    operation: string
    propertyType: string
    priceAmount: number
    priceCurrency: string
    status: string
    url: string
    title: string
    description: string
    sellerInfo: {
      name: string
      email: string | null
      phone: string
      whatsapp: string
    }
    commissionRate?: number
    host: string
    sourceId: string
    publishedDate: string
    associationIds: string | null
  }>
}

// Mapeo de tipos de construcción a tipos de propiedad
const buildTypeMapping: Record<string, string> = {
  // Apartment
  'apartment': 'apartment',

  // Commercial
  'shop': 'commercial',
  'shop_shopping_center': 'commercial',
  'hotel': 'commercial',
  'event_hall': 'commercial',

  // House
  'house': 'house',
  'ranch': 'house',
  'cottage': 'house',

  // Industrial
  'industrial_building': 'industrial',
  'other': 'industrial',

  // Land
  'terrain': 'land',

  // Office
  'office': 'office',
  'consulting_room': 'office',
  'building': 'office',

  // Warehouse
  'warehouse': 'warehouse'
}

const mapBuildTypeToPropertyType = (buildTypeString: string): string[] => {
  if (!buildTypeString) return []

  const buildTypes = buildTypeString.split(',').map(type => type.trim())
  const mappedTypes = buildTypes
    .map(type => buildTypeMapping[type])
    .filter(type => type) // Filtrar valores undefined

  // Retornar valores únicos
  return [...new Set(mappedTypes)]
}

export const convertParamsToNewAPI = (params: IGetPropertiesByParametersParams): Record<string, any> => {
  const {
    // Parámetros que cambian de nombre
    buildType,
    builtYearFrom,
    builtYearTo,
    commissionRateHighRange,
    commissionRateLowRange,
    neighborhoods,
    showOnlyCommissionProperties,
    surfaceFrom,
    surfaceTo,
    type,

    // Parámetros que mantienen el mismo nombre
    amenities,
    arrayCoordinates,
    associationIds,
    bathrooms,
    bedrooms,
    budgetHighRange,
    budgetLowRange,
    budgetM2HighRange,
    budgetM2LowRange,
    caprateHighRange,
    caprateLowRange,
    latitude,
    listingsSource,
    longitude,
    opportunityCriteria,
    page,
    parkings,
    perPage,
    searchLevel,
    sortCriteria,

    // Parámetros que no se mapean a la nueva API
    address,
    allowEmptyCoordinates,
    boundsFilter,
    coldStart,
    discountHighRange,
    discountLowRange,
    drawingFilter,
    geocertainty,
    keywords,
    origin,
    pamRequestId,
    presale,
    proxyPublishedAt,
    proxyPublishedDays,
    quickLinkState,
    radiusMeters,
    searchAddress,
    sessions,
    wiggotUserType,
  } = params

  const newParams: Record<string, any> = {}

  if (buildType) {
    const mappedPropertyTypes = mapBuildTypeToPropertyType(buildType)
    if (mappedPropertyTypes.length > 0) {
      newParams['propertyType'] = mappedPropertyTypes.join(',')
    }
  }

  if (type) {
    newParams['operation'] = type
  }

  if (showOnlyCommissionProperties === true) {
    newParams['withCommissions'] = true
  }

  if (neighborhoods) {
    newParams['neighborhoodId'] = neighborhoods
  }

  if (commissionRateLowRange !== undefined && commissionRateLowRange > 0) {
    newParams['minCommissionRate'] = commissionRateLowRange
  }

  if (commissionRateHighRange !== undefined && commissionRateHighRange > 0) {
    newParams['maxCommissionRate'] = commissionRateHighRange
  }

  if (builtYearFrom !== undefined && builtYearFrom > 0) {
    newParams['builtYearFrom'] = builtYearFrom
  }

  if (builtYearTo !== undefined && builtYearTo > 0) {
    newParams['builtYearTo'] = builtYearTo
  }

  if (surfaceFrom !== undefined && surfaceFrom > 0) {
    newParams['surfaceFrom'] = surfaceFrom
  }

  if (surfaceTo !== undefined && surfaceTo > 0) {
    newParams['surfaceTo'] = surfaceTo
  }

  // Parámetros que mantienen el mismo nombre
  if (page !== undefined) newParams['page'] = page
  if (perPage !== undefined) newParams['perPage'] = perPage
  if (searchLevel) newParams['searchLevel'] = searchLevel
  if (latitude !== undefined) newParams['latitude'] = latitude
  if (longitude !== undefined) newParams['longitude'] = longitude
  // arrayCoordinates ya llega como el string plano "[lng,lat],[lng,lat]" armado
  // por quien arma el request (ver PropertiesLoader/PointsProvider). No se
  // reconstruye ni se reinterpreta aquí: solo se reenvía tal cual.
  if (arrayCoordinates) newParams['arrayCoordinates'] = arrayCoordinates
  if (associationIds) newParams['associationIds'] = associationIds
  if (listingsSource) newParams['listingsSource'] = listingsSource
  if (bedrooms !== undefined && bedrooms > 0) newParams['bedrooms'] = bedrooms
  if (bathrooms !== undefined && bathrooms > 0) newParams['bathrooms'] = bathrooms
  if (parkings !== undefined && parkings > 0) newParams['parkings'] = parkings

  const hasValidBudgetLow = budgetLowRange !== undefined && budgetLowRange > 0
  const hasValidBudgetHigh = budgetHighRange !== undefined && budgetHighRange > 0

  if (hasValidBudgetLow) {
    newParams['budgetLowRange'] = budgetLowRange
  }
  if (hasValidBudgetHigh && (!hasValidBudgetLow || budgetHighRange! > budgetLowRange!)) {
    newParams['budgetHighRange'] = budgetHighRange
  }

  const hasValidBudgetM2Low = budgetM2LowRange !== undefined && budgetM2LowRange > 0
  const hasValidBudgetM2High = budgetM2HighRange !== undefined && budgetM2HighRange > 0

  if (hasValidBudgetM2Low) {
    newParams['budgetM2LowRange'] = budgetM2LowRange
  }
  if (hasValidBudgetM2High && (!hasValidBudgetM2Low || budgetM2HighRange! > budgetM2LowRange!)) {
    newParams['budgetM2HighRange'] = budgetM2HighRange
  }

  const hasValidCaprateLow = caprateLowRange !== undefined && caprateLowRange > 0
  const hasValidCaprateHigh = caprateHighRange !== undefined && caprateHighRange > 0

  if (hasValidCaprateLow) {
    newParams['caprateLowRange'] = caprateLowRange
  }
  if (hasValidCaprateHigh && (!hasValidCaprateLow || caprateHighRange! > caprateLowRange!)) {
    newParams['caprateHighRange'] = caprateHighRange
  }

  if (amenities) newParams['amenities'] = amenities
  if (opportunityCriteria) newParams['opportunityCriteria'] = opportunityCriteria
  if (sortCriteria) newParams['sortCriteria'] = sortCriteria

  return newParams
}

const convertResponseToOldFormat = (newResponse: INewAPIResponse): IPropertiesResponse => {
  const { pagination, items: newItems } = newResponse

  const convertedItems: IProperty[] = newItems.map((newItem) => {
    const firstAd = newItem.ads?.[0]
    const wiggotAd = newItem.ads.find((ad) => ad.host.includes('wiggot'))

    const addressParts = newItem.address.split(', ')
    const addressNeighborhood = addressParts[1] || ''
    const addressMunicipality = addressParts[2] || ''
    const addressState = addressParts[3] || ''

    const convertedItem: IProperty = {
      id: newItem.id,
      createdAt: firstAd?.publishedDate || new Date().toISOString(),
      listingType: (firstAd?.operation || 'for-sale') as EPropertyType,
      propertyType: newItem.propertyType as 'apartment' | 'house',
      latitude: newItem.coordinates.y,
      longitude: newItem.coordinates.x,
      surfaceTotal: parseFloat(newItem.constructionSurface) || 0,
      terrainSurface: parseFloat(newItem.terrainSurface) || 0,
      numBedrooms: newItem.bedrooms,
      numBathrooms: parseFloat(newItem.bathrooms) || 0,
      numParkingLots: 0, // No disponible en nueva API, usar 0 por defecto
      price: parseFloat(newItem.priceAmount) || (firstAd?.priceAmount || 0),
      addressMunicipality: addressMunicipality,
      addressState: addressState,
      proxyPublishedAt: firstAd?.publishedDate || new Date().toISOString(),
      coverImage: newItem.media?.[0]?.url || '',
      selectedId: firstAd?.sourceId || newItem.id,
      providerId: newItem.isWiggotProperty ? 1 : 2,
      isWiggotProperty: newItem.isWiggotProperty,
      wiggotMetadata: newItem.isWiggotProperty && wiggotAd ? {
        id: wiggotAd.sourceId ?? '',
        commissionRate: wiggotAd?.commissionRate || 0,
      } : undefined,
      // Propiedades opcionales mapeadas
      floorNumber: newItem.surfaceDetails?.floors || undefined,
      addressStreet: addressParts[0] || newItem.address,
      addressNumber: '',
      addressNeighborhood,
      description: firstAd?.description || '',
      petsAllowed: false,
      forRentSoonToBe: undefined,

      // Propiedades requeridas con valores por defecto
      ABGroup: '',
      attributes: [],
      builtYear: 0,
      geocertainty: 'low' as const,
      highlights: {},
      itemId: newItem.id,
      listingsOwners: [],
      photos: newItem.media?.slice(1).map(m => m.url) || [],
      recommendationId: '',
      sepomexId: '',
      shareUrl: '',
      status: true,
      tag: null,
      type: (firstAd?.operation || 'for-sale') as EPropertyType,
      visitDate: 0,
      seller: firstAd?.sellerInfo?.name || '',
      phone: firstAd?.sellerInfo?.phone || '',
      features: [],
      adIdPictures: newItem.id,
      listings: [],

      // Propiedades adicionales con valores por defecto
      balconySurface: 0,
      capitalizationRate: null,
      cveEnt: '',
      cveGeo: '',
      cveMun: '',
      deletedAt: '',
      discountFromPrediction: 0,
      discountFromPredictionPercent: 0,
      externalId: newItem.id,
      flagBannedScraper: false,
      fullAddress: newItem.address,
      isDevelopment: false,
      isDirectDeal: false,
      isDiscountedFromPrediction: false,
      isNewPropertyProb: 0,
      isPresale: false,
      lastOffMarketDate: '',
      lastOffMarketPrice: '',
      luxuryCategoryBathrooms: '',
      numFloors: 1,
      onMarket: true,
      onMarketDs: true,
      owners: [],
      predictionPricePerSquareMeterLower: 0,
      predictionPricePerSquareMeterRent: newItem.surfaceDetails?.pricePerSquareMeter || 0,
      predictionPricePerSquareMeterSale: newItem.surfaceDetails?.pricePerSquareMeter || 0,
      predictionPricePerSquareMeterUpper: 0,
      predictionPriceRent: 0,
      predictionPriceSale: 0,
      propertyCode: '',
      propertyInteriorCode: '',
      shortAddress: addressParts[0] || newItem.address,
      timeOnMarketDs: '',
      updatedAt: new Date().toISOString()
    }

    return convertedItem
  })

  return {
    currentPage: pagination.page,
    globalCount: pagination.totalItems,
    items: convertedItems,
    perpage: pagination.perPage,
    totalItems: pagination.totalItems,
    totalPages: pagination.totalPages
  }
}

export async function getPropertiesByParameters(
  params: IGetPropertiesByParametersParams
): Promise<IHttpResponse<IPropertiesResponse>> {
  try {
    const convertedParams = convertParamsToNewAPI({
      ...params,
      perPage: params.perPage ?? 24
    })

    // listingsBasicApi.get ya devuelve el body de la respuesta (no el
    // AxiosResponse completo), a diferencia del propertyBasicAPI que
    // usaba monopolio-ui.
    const newAPIResponse: INewAPIResponse = await listingsBasicApi.get(
      '/properties/wiggot',
      { params: convertedParams }
    )

    const convertedResponse = convertResponseToOldFormat(newAPIResponse)
    return {
      isSuccess: true,
      data: convertedResponse,
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error,
    }
  }
}

export async function getPhotosByProperty(params: {
  id: string
}): Promise<IHttpResponse<IPropertyPhoto[]>> {
  try {
    const { id } = params
    const response: any = await listingsBasicApi.get(
      `/listings-properties/public/photos/ads/${id}`
    )
    return {
      isSuccess: true,
      data: response?.data,
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error,
    }
  }
}

export const getPropertiesSummary = async (
  queryParams: string
): Promise<
  IHttpResponse<{
    totalCount: number
    avgPrice: number
    avgM2Price: number
    avgSurface: number
  }>
> => {
  try {
    const response: any = await listingsBasicApi.get(
      `/listings/filter-summary${queryParams}`
    )
    return {
      isSuccess: true,
      data: response?.data,
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error,
    }
  }
}
